import { clientPosToWorldPos, getUniqueId } from '@/editor/utils';
import { EEntityType } from '@/editor/types';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { CommonController2d } from '../CommonController';
import { IAssemblyData } from '@/editor/data/assembly/assembly';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { AssemblyAbsorbManager } from '@/editor/manager/absorb/box/assemblyAbsorbManager';
import { calculateMatrix, relativeToWorldInfo } from '@/editor/math/utils';
import { Vector3 } from 'three';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';
export class DrawAssembly extends CommonController2d {
  private _assemblyEntityMap: Map<string, FurnitureEntity> = new Map();

  /**
   * 复制源的数据快照，配件的相对变换以它为基准。
   * 禁止跟着参数面板的改动更新，否则相对变换的基准与配件的实际位置对不上，旋转会被抵消掉
   */
  private _sourceData: IAssemblyData | null = null;
  private _virtualData: IAssemblyData | null = null;

  private _rulerModel = new RulerModel();
  private _rayDetectOutlines: { start: IPoint2d; end: IPoint2d }[] = [];
  private _assemblyAbsorbManager = new AssemblyAbsorbManager();

  private _selectedBoxes = new OperateSelectedBoxes();

  constructor(_gs: TGlobalStores) {
    super(_gs);
  }

  override init() {
    super.init();

    this._assemblyAbsorbManager.updateData({
      excludeAssemblyIds: []
    });
    this._updateRayDetectOutlines();

    const virtualData = this._gs.editorStateStore.assemblyVirtualData;
    if (!virtualData) return;

    this._sourceData = virtualData;
    this._virtualData = { ...virtualData, id: getUniqueId() };
    this.updateBoxes();
  }

  private _updateRayDetectOutlines() {
    this._rayDetectOutlines = getBoxRayDetectOutlines();
  }

  /**
   * 参数面板把改动写在 assemblyVirtualData 上（与虚拟家具一致），
   * 因此每次取用都读最新值，而不是缓存 init 时的那一份
   */
  private get editingData() {
    return this._gs.editorStateStore.assemblyVirtualData ?? this._sourceData;
  }

  private updateVirsualAssembly() {
    if (!this._virtualData) return;

    const { accessorieIds } = this._virtualData;
    if (!accessorieIds.length) {
      this.clearAssemblyEntity();
    }

    this.updateEntityInfos();
    const rulerInfo = calculateBoxRulerInfos(this._virtualData, this._rayDetectOutlines);
    this._rulerModel.updateModel(rulerInfo);
    this.updateBoxes({
      x: this._virtualData.center.x,
      y: this._virtualData.center.y,
      rotation: this._virtualData.rotation
    });
  }

  private updateEntityInfos() {
    if (!this._virtualData) return;

    const moveAssemblyData = this._virtualData;

    const virtualAssemblyData = this._sourceData;
    if (virtualAssemblyData) {
      // 组合原始矩阵
      const assemblyMatrix = calculateMatrix(
        new Vector3(0, 0, 0),
        new Vector3(virtualAssemblyData.center.x, virtualAssemblyData.center.y, 0),
        0,
        virtualAssemblyData.rotation
      );

      // 移动后的矩阵
      const assemblyMoveMatrix = calculateMatrix(
        new Vector3(0, 0, 0),
        new Vector3(moveAssemblyData.center.x, moveAssemblyData.center.y, 0),
        0,
        moveAssemblyData.rotation
      );

      const furnitureDatas = globalStores.assemblyOperation.getAssemblyFurnitureDatas(virtualAssemblyData);
      for (const furnitureData of furnitureDatas) {
        const furnitureMatrix = calculateMatrix(
          new Vector3(0, 0, 0),
          new Vector3(furnitureData.center.x, furnitureData.center.y, 0),
          0,
          furnitureData.rotation
        );

        // 将家具矩阵furnitureMatrix转换为组合坐标系下的矩阵
        const relativeMatrix = assemblyMatrix.clone().invert().multiply(furnitureMatrix);
        const { position, rotation } = relativeToWorldInfo(relativeMatrix, assemblyMoveMatrix);

        furnitureData.center = { x: position.x, y: position.y };
        furnitureData.rotation = rotation.z;

        let furnitureEntity = this._assemblyEntityMap.get(furnitureData.id);
        if (!furnitureEntity) {
          furnitureEntity = new FurnitureEntity({ id: furnitureData.id + 'assembly' });
          furnitureEntity.update(furnitureData);
          this._assemblyEntityMap.set(furnitureData.id, furnitureEntity);
        } else {
          furnitureEntity.update(furnitureData);
        }
      }
    }
  }

  private getAssemblyBoxData(point: IPoint2d, assemblyData: IAssemblyData) {
    const boxData = {
      center: point,
      width: assemblyData.width,
      thickness: assemblyData.thickness,
      rotation: assemblyData.rotation
    };
    const absorbResult = this._assemblyAbsorbManager.calculateThenRender(boxData);
    if (absorbResult) {
      boxData.center = absorbResult.absorbPoint;
      boxData.rotation = absorbResult.rotation;
    }

    return boxData;
  }

  protected override mouseMove = (e: MouseEvent) => {
    const editingData = this.editingData;
    if (!editingData) return;

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const mousePosition = clientPosToWorldPos(e, camera, renderer);

    const boxData = this.getAssemblyBoxData(mousePosition, editingData);
    this._virtualData = { ...editingData, ...boxData };

    this.updateVirsualAssembly();
  };

  protected override mouseUp = (e: MouseEvent, isDragged: boolean) => {
    if (!this._virtualData) return;

    if (isDragged) return;

    if (e.button === 2) {
      this.dispose();
      this.exit();
      return;
    }

    if (e.button !== 0) return;

    this._gs.assemblyOperation.copyAssemblyData(this._virtualData);
    this._assemblyAbsorbManager.clearModel();
    this._assemblyAbsorbManager.updateData({ excludeAssemblyIds: [] });
    this._updateRayDetectOutlines();
  };

  private clearAssemblyEntity() {
    for (const entity of this._assemblyEntityMap.values()) {
      entity.dispose();
    }
    this._assemblyEntityMap.clear();
    this._rulerModel.clear();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._sourceData?.id) return;

    this._selectedBoxes.update({ type: EEntityType.Assembly, entityId: this._sourceData.id }, params);
  }

  clear() {
    this.clearAssemblyEntity();
    this._virtualData = null;
    this._sourceData = null;
    this._selectedBoxes.clear();
    this._rulerModel.clear();
    this._assemblyAbsorbManager.clearModel();
  }

  override dispose() {
    this.clear();
    super.dispose();

    this._assemblyAbsorbManager.dispose();
    this._selectedBoxes.dispose();
    this._rulerModel.dispose();
  }
}
